import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import {
  DocumentFileTemplateFormDto,
  DocumentFileTemplateFormInterface
} from '@zaoblako/shared/modules/catalog/dtos/document-file-template/document-file-template-form.dto';
import { Observable } from 'rxjs';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { BasisDocumentFileTemplateService } from '@zaoblako/web/core/services/basis/basis-document-file-template.service';
import { DocumentFileTemplateFindInterface } from '@zaoblako/shared/modules/catalog/dtos/document-file-template/document-file-template-find.dto';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { AsyncPipe } from '@angular/common';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { CardModule } from 'primeng/card';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { FileUploaderComponent } from '@zaoblako/web/shared/components/file-uploader/file-uploader.component';
import type { FileFindInterface } from '@zaoblako/shared/modules/storage/dtos/file/file-find.dto';
import { FileComponent } from '@zaoblako/web/shared/dumbs/file/file.component';
import { DocumentFileTemplateLogsComponent } from '@zaoblako/web/modules/directory/dumbs/document-file-template-logs/document-file-template-logs.component';
import { DocumentFileTemplateTypeEnum } from '@zaoblako/shared/modules/catalog/enums/document-file-template-type.enum';

@Component({
  selector: 'crm-document-file-template-form',
  standalone: true,
  imports: [
    SpinnerComponent,
    AsyncPipe,
    ButtonComponent,
    InputTextComponent,
    CardModule,
    JoinPipe,
    FileUploaderComponent,
    FileComponent,
    DocumentFileTemplateLogsComponent
  ],
  templateUrl: './document-file-template-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentFileTemplateFormComponent
  extends BaseFormComponent<DocumentFileTemplateFormInterface>
  implements OnInit {
  @Input() public readonly index: number;
  public document: DocumentFileTemplateFindInterface = <DocumentFileTemplateFindInterface>{};

  public dto = DocumentFileTemplateFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public constructor(
    private readonly basisDocumentFileTemplateService: BasisDocumentFileTemplateService,
    public readonly formStoreService: FormStoreService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService
  ) {
    super();
  }

  public ngOnInit() {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    this.document.type = DocumentFileTemplateTypeEnum.DEAL;

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.basisDocumentFileTemplateService
      .findById<DocumentFileTemplateFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (document) => {
          this.document = document;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveDocument() {
    if (this.checkValidate(this.document).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.basisDocumentFileTemplateService
          .update<DocumentFileTemplateFormInterface, DocumentFileTemplateFindInterface>(this.document)
          .subscribe({
            next: (document) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`Шаблон документа "${this.document.name}" обновлен`);
              this.formStoreService.saveNext(document, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.basisDocumentFileTemplateService.create<DocumentFileTemplateFindInterface>(this.document).subscribe({
          next: (document) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Шаблон документа создан');
            this.formStoreService.saveNext(document, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteDocument() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить шаблон документа "${this.document.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.basisDocumentFileTemplateService
          .deleteById(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe({
            next: () => {
              this.errorService.addSuccessMessage(`Шаблон документа "${this.document.name}" удален`);
              this.formStoreService.deleteNext(this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      }
    });
  }

  public restoreDocument() {
    this.confirmService.confirm({
      message: `Вы действительно хотите восстановить шаблон документа "${this.document.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.basisDocumentFileTemplateService
          .restore(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe({
            next: () => {
              this.document = { ...this.document, deleted: null };
              this.cdRef.markForCheck();
              this.errorService.addSuccessMessage(`Шаблон документа "${this.document.name}" восстановлен`);
              this.formStoreService.saveNext(this.document, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      }
    });
  }

  public setFile(files: FileFindInterface[]) {
    this.document.file = files[0];
  }
}
